/**
 * Alignment Map Legend Component
 * Visual guide showing node types, colors, and interaction hints
 */

import React, { useState } from 'react';
import { Building2, Landmark, Users, User, ChevronDown, ChevronUp, Info } from 'lucide-react';
import { Badge } from '../common/Badge';

export const AlignmentMapLegend: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(false);

  const nodeTypes = [
    {
      icon: Building2,
      label: 'Company OKR',
      color: 'bg-blue-500',
      textColor: 'text-blue-700',
      bgLight: 'bg-blue-50',
    },
    {
      icon: Landmark,
      label: 'Department OKR',
      color: 'bg-amber-500',
      textColor: 'text-amber-700',
      bgLight: 'bg-amber-50',
    },
    {
      icon: Users,
      label: 'Team OKR',
      color: 'bg-green-500',
      textColor: 'text-green-700',
      bgLight: 'bg-green-50',
    },
    {
      icon: User,
      label: 'Individual OKR',
      color: 'bg-purple-500',
      textColor: 'text-purple-700',
      bgLight: 'bg-purple-50',
    },
  ];

  const statusTypes = [
    { variant: 'success' as const, label: 'On Track / Completed' },
    { variant: 'warning' as const, label: 'At Risk' },
    { variant: 'danger' as const, label: 'Off Track' },
    { variant: 'secondary' as const, label: 'Draft' },
  ];

  return (
    <div className="absolute bottom-4 left-4 z-30 bg-white rounded-lg shadow-lg border border-stone-200 overflow-hidden max-w-xs">
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-2 bg-stone-50 border-b border-stone-200 flex items-center justify-between hover:bg-stone-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Info className="w-4 h-4 text-stone-600" />
          <span className="text-sm font-semibold text-stone-900">Legend</span>
        </div>
        {isExpanded ? (
          <ChevronDown className="w-4 h-4 text-stone-600" />
        ) : (
          <ChevronUp className="w-4 h-4 text-stone-600" />
        )}
      </button>

      {/* Content */}
      {isExpanded && (
        <div className="p-4 space-y-4">
          {/* Node Types */}
          <div>
            <h4 className="text-xs font-semibold text-stone-700 uppercase tracking-wide mb-2">
              OKR Types
            </h4>
            <div className="space-y-2">
              {nodeTypes.map((type) => (
                <div key={type.label} className="flex items-center gap-2">
                  <div className={`w-8 h-8 ${type.color} rounded flex items-center justify-center flex-shrink-0`}>
                    <type.icon className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-xs text-stone-700">{type.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Status Types */}
          <div className="pt-3 border-t border-stone-200">
            <h4 className="text-xs font-semibold text-stone-700 uppercase tracking-wide mb-2">
              Status
            </h4>
            <div className="space-y-2">
              {statusTypes.map((status) => (
                <div key={status.label} className="flex items-center gap-2">
                  <Badge variant={status.variant} size="sm">
                    {status.label.split(' / ')[0]}
                  </Badge>
                  <span className="text-xs text-stone-600">{status.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Interaction Hints */}
          <div className="pt-3 border-t border-stone-200">
            <h4 className="text-xs font-semibold text-stone-700 uppercase tracking-wide mb-2">
              Interactions
            </h4>
            <div className="space-y-1.5 text-xs text-stone-600">
              <p>• <strong>Click</strong> node to view details</p>
              <p>• <strong>Hover</strong> node for quick info</p>
              <p>• <strong>Drag</strong> to pan the map</p>
              <p>• <strong>Scroll</strong> to zoom in/out</p>
            </div>
          </div>

          {/* Connection Info */}
          <div className="pt-3 border-t border-stone-200">
            <h4 className="text-xs font-semibold text-stone-700 uppercase tracking-wide mb-2">
              Connections
            </h4>
            <div className="space-y-1.5 text-xs text-stone-600">
              <div className="flex items-center gap-2">
                <div className="w-12 h-0.5 bg-stone-400"></div>
                <span>Alignment relationship</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-12 h-0.5 bg-primary-500"></div>
                <span>Selected connection</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
